import { DefaultSelected, SimpleModal, UpsertRDOrderGrid } from "../../components";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Order } from "src/app/types/orders/order";
import { SiteTransport } from "src/app/transports/site";
import {
    useGetOrderById,
    useListAvailableByRD,
    usePBNOptions,
    useRDSubmitHandler,
} from "src/app/hooks";
import { Site, UpsertSiteDTO } from "src/app/types/Site";
import { toast } from "react-toastify";
import { Col, Form, Row } from "react-bootstrap";
import { IActionsModal } from "src/app/types/actionsModal";
import Pacman from "src/app/components/layout/Pacman";

export interface IUpsertRDOrderModalContainer {
    handleClose?: () => {};
    entity: Order;
    updateGridOnSave?: (object: any, isNew: boolean, removeRow?: boolean | undefined) => void;
}
const UpsertRDOrderModalContainer = (props: IActionsModal<Order>) => {
    const { handleClose = () => {}, entity, updateGridOnSave = () => {} } = props;
    const { formatMessage } = useIntl();

    const [selectedRows, setSelectedRows] = useState<Array<Site>>([]);
    const [rdToChangePBN, setRdToChangePBN] = useState<Site>();
    const [showPBNModal, setShowPBNModal] = useState(false);
    const [newPbn, setNewPbn] = useState<number>();
    const [price, setPrice] = useState<number | null | undefined>();

    const { order, loading: loadingOrder } = useGetOrderById(entity?.id);

    const { submit, loading } = useRDSubmitHandler({
        updateGridOnSave,
        handleClose,
    });
    const { PBNOptions, loading: PBNOptionsLoading, refresh } = usePBNOptions(undefined, false);
    const {
        siteList,
        loading: siteListLoading,
        refresh: refreshSiteList,
    } = useListAvailableByRD({ moneySiteId: order?.moneySite?.id }, false);

    const handleSubmit = () => {
        if (!order) return;
        submit(selectedRows, order);
    };

    useEffect(() => {
        if (!order) return;
        refreshSiteList({ moneySiteId: order?.moneySite?.id });
    }, [order]);

    const handlePBNModal = (open: boolean, site?: Site) => {
        if (open) {
            if (!site) {
                throw new SyntaxError("if open, site required");
            }
            setRdToChangePBN(site);
            if (PBNOptions.length === 0) refresh();
            setNewPbn(site.pbn.id);
            setPrice(site.price);
        }
        setShowPBNModal(open);
    };

    const handlePBNChange = async () => {
        if (!rdToChangePBN) {
            toast.error(formatMessage({ id: "common.notification.error" }));
            return;
        }
        if (!newPbn) {
            toast.error(formatMessage({ id: "common.notification.error" }));
            return;
        }
        const upsertSite = {
            ...SiteTransport.formTransformer(rdToChangePBN),
            pbnId: newPbn,
            price,
        };
        try {
            await SiteTransport.save(upsertSite as UpsertSiteDTO, rdToChangePBN.id);
            handlePBNModal(false);
            refreshSiteList();
            toast.success(formatMessage({ id: "common.notification.success" }));
        } catch (error) {
            toast.error(formatMessage({ id: "common.notification.error" }));
        }
    };

    return (
        <>
            <SimpleModal
                entityName={formatMessage({ id: "common.entity.chooseRD" })}
                handleClose={handleClose}
                buttonConfirmationAction={handleSubmit}
                disableConfirmation={selectedRows.length == 0}
                loading={siteListLoading || loadingOrder}
                formId="rdOrder"
                buttonConfirmationLabel={formatMessage({ id: "common.action.confirmation" })}
                showModal={true}
                modalProps={{ fullscreen: true }}
            >
                {!order ? (
                    <Pacman />
                ) : (
                    <UpsertRDOrderGrid
                        handlePBNModal={handlePBNModal}
                        entity={order}
                        setSelectedRows={setSelectedRows}
                        siteList={siteList}
                    />
                )}
            </SimpleModal>
            {showPBNModal && (
                <SimpleModal
                    entityName={formatMessage({ id: "common.entity.site" })}
                    handleClose={() => handlePBNModal(false)}
                    buttonConfirmationAction={handlePBNChange}
                    loading={loading}
                    formId="changePbn"
                    buttonConfirmationLabel={formatMessage({ id: "common.action.confirmation" })}
                    showModal={showPBNModal}
                    modalProps={{ size: "sm" }}
                >
                    <Row className="gap-4">
                        <Row>
                            <Col sm={4}>
                                <label>{formatMessage({ id: "common.entity.pbn" })}:</label>
                            </Col>
                            <Col>
                                <DefaultSelected
                                    value={newPbn}
                                    options={PBNOptions}
                                    isLoading={PBNOptionsLoading}
                                    onChange={(value) => {
                                        if (typeof value !== "string") setNewPbn(value);
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={4}>
                                <label>{formatMessage({ id: "common.entity.price" })}:</label>
                            </Col>
                            <Col>
                                <div style={{ marginLeft: "8px" }}>
                                    <Form.Control
                                        min={0}
                                        type="number"
                                        value={price || 0}
                                        onChange={(el) =>
                                            setPrice(parseFloat(el.currentTarget.value))
                                        }
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Row>
                </SimpleModal>
            )}
        </>
    );
};

export default UpsertRDOrderModalContainer;
